/** ECRANUL «IPOTEZE»: ce decide dezvoltatorul, despărțit de ce se măsoară.
 *
 *  Rezumatul avea o secțiune «Ipoteze» de șaptesprezece rânduri, în care cinci
 *  erau decizii și restul măsurători, formule și repere — toate la fel de
 *  ipoteze, după cum arătau. Aici stau în trei locuri: deciziile, singurele care
 *  se scriu pe ecranul ăsta; ce vine din proiect, cu locul de unde se schimbă;
 *  și reperele de piață, cu sursa lor.
 *
 *  Primul ecran al paginii până le confirmă cineva: rezultatele pornesc de la
 *  ele, iar o cifră de profit pe un preț pe care nu l-a ales nimeni arată la fel
 *  de sigur ca una pe un preț ales. Se confirmă toate odată (jos) sau câte una,
 *  pe rândul ei: valoarea implicită poate fi chiar cea bună, și atunci a o
 *  rescrie identic ca s-o «aleagă» n-ar spune nimic.
 */
import { useEffect, useState, type ReactNode } from "react";
import Termeni, { Estimat, Lamurire, Note } from "./Termeni";
import { numar, tn, useT } from "../../i18n";
import type { Cheie } from "../../i18n";
import { bani, baniMilioane } from "../../structure/bani";
import { DECIZII, eEstimat, type Decizie, type Ipoteze, type Rand, type Rezumat } from "./businessCase";
import type { Recomandare } from "./piata";
import type { Scenarii, Semnatura } from "./scenarii";

/** Cum se scrie fiecare decizie: eticheta, unitatea (`{loc}`, `{ani}` = cuvântul
 *  din catalog), pasul, și dacă e procent. */
const FORMA: Record<Decizie, { eticheta: Cheie; um: string; pas: number; procent?: boolean;
                               ajutor?: Cheie }> = {
  cedatDinScd: { eticheta: "bc.r.cedat.element", um: "%", pas: 1, procent: true,
                 ajutor: "ip.ajutor.cedat" },
  pretTerenBani: { eticheta: "ip.pret_teren_bani", um: "€", pas: 10000, ajutor: "ip.ajutor.teren_bani" },
  costuriTeren: { eticheta: "bc.r.cTeren.element", um: "€", pas: 1000, ajutor: "bc.r.cTeren.obs" },
  pretApartamenteMp: { eticheta: "bc.ui.pret_apartamente", um: "€/m²", pas: 50,
                       ajutor: "ip.ajutor.pret_ap" },
  pretTeraseMp: { eticheta: "bc.r.pTerase.element", um: "€/m²", pas: 50 },
  pretComercialMp: { eticheta: "bc.r.pCom.element", um: "€/m²", pas: 50 },
  pretParcareLoc: { eticheta: "bc.r.pParc.element", um: "€/{loc}", pas: 500 },
  duratiAni: { eticheta: "bc.r.durata.element", um: "{ani}", pas: 0.5, ajutor: "bc.r.durata.obs" },
  // THE FINANCING AND THE SALES (Stefan, 4.11.2026): the loan as a share of the
  // cost, the bank's margin (Euribor is a reference beside it), the fee, the
  // share cashed at completion and the months the rest takes
  creditDinCostPct: { eticheta: "ip.credit_din_cost", um: "% {din_cost}", pas: 5, procent: true, ajutor: "ip.ajutor.credit" },
  dobandaMarjaPct: { eticheta: "ip.dobanda", um: "% {pe_an}", pas: 0.25, procent: true, ajutor: "ip.ajutor.dobanda" },
  comisionCreditPct: { eticheta: "ip.comision_credit", um: "%", pas: 0.25, procent: true, ajutor: "ip.ajutor.comision_credit" },
  vandutLaReceptiePct: { eticheta: "ip.ritm", um: "%", pas: 5, procent: true },
  perioadaVanzareLuni: { eticheta: "ip.ritm", um: "{luni}", pas: 1 },
};

/** The assumptions written here that are not decisions to confirm: a market
 *  reference (Euribor), a switch and a display band. */
type Liber = "euriborPct" | "bandaPretPct";
const FORMA_LIBERA: Record<Liber, { eticheta: Cheie; pas: number }> = {
  euriborPct: { eticheta: "bc.r.euribor.element", pas: 0.1 },
  bandaPretPct: { eticheta: "ip.banda_pret", pas: 1 },
};

/** Rândurile rezumatului care SUNT decizii — nu se mai arată a doua oară jos. */
const RANDURI_DECIZII = ["cedat", "pTerase", "pCom", "pParc", "durata", "cTeren", "cTerenNatura",
  "credit", "dobanda", "pComisionCredit", "vanzariReceptie", "vanzariPerioada", "bandaPret"];

const unitatea = (um: string, t: ReturnType<typeof useT>) => um
  .replace("{loc}", t("bc.um.loc")).replace("{ani}", t("bc.um.ani")).replace("{luni}", t("bc.um.luni"))
  .replace("{din_cost}", t("ip.um.din_cost")).replace("{pe_an}", t("ip.um.pe_an"));
/** Sursele care sunt ale proiectului (sau calculate din ele). */
const DIN_PROIECT = ["acte", "puz", "proiect", "tema", "formula"];

/** Un câmp numeric care se salvează la ieșire sau la Enter, nu la fiecare tastă:
 *  «2 250» scris cifră cu cifră ar fi făcut patru scenarii pe server. */
function Camp({ k, valoare, implicit, poate, verificate, validata, laSchimbare, laValidare }: {
  k: Decizie; valoare: number; implicit: number; poate: boolean; verificate: boolean;
  /** Semnătura ipotezei confirmate pe rând, dacă e. */
  validata: Semnatura | null;
  laSchimbare: (k: Decizie, v: number) => void;
  laValidare: (k: Decizie) => void;
}) {
  const t = useT();
  const f = FORMA[k];
  const afisat = f.procent ? Math.round(valoare * 1000) / 10 : valoare;
  const [ciorna, setCiorna] = useState(String(afisat));
  useEffect(() => { setCiorna(String(afisat)); }, [afisat]);
  const trimite = () => {
    const v = Number(ciorna.replace(",", "."));
    if (!Number.isFinite(v) || v < 0) { setCiorna(String(afisat)); return; }
    const real = f.procent ? v / 100 : v;
    if (real !== valoare) laSchimbare(k, real);
  };
  const eImplicit = valoare === implicit;
  // o valoare schimbată a ales-o cineva; numai implicita are ce confirma
  const deValidat = !verificate && eImplicit && !validata;
  return (
    <span className="ip-ctl">
      {deValidat && <span className="ip-pill ip-pill-amb">{t("ip.implicit")}</span>}
      {deValidat && poate && (
        <button type="button" className="btn ip-valideaza" onClick={() => laValidare(k)}>
          {t("ip.valideaza")}</button>
      )}
      {!verificate && eImplicit && validata && (
        <span className="ip-pill ip-pill-ok" title={t("ip.validata_de", { de: validata.de || "—",
          la: validata.la ? new Date(validata.la).toLocaleDateString() : "" })}>
          {t("ip.validata")}</span>
      )}
      {!eImplicit && <span className="ip-pill ip-pill-acc">{t("ip.modificat")}</span>}
      <span className={`ip-inp${deValidat ? " ip-inp-def" : ""}`}>
        <input type="number" inputMode="decimal" step={f.pas} min={0} value={ciorna}
               aria-label={t(f.eticheta)} disabled={!poate}
               onChange={(e) => setCiorna(e.target.value)} onBlur={trimite}
               onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
        <span>{unitatea(f.um, t)}</span>
      </span>
    </span>
  );
}

/** A free numeric assumption: saved on blur or Enter, no confirmation pill. */
function CampLiber({ k, valoare, poate, procent, um, laSchimbare }: {
  k: Liber; valoare: number; poate: boolean; procent?: boolean; um: string;
  laSchimbare: (k: Liber, v: number) => void;
}) {
  const t = useT();
  const afisat = procent ? Math.round(valoare * 10000) / 100 : valoare;
  const [ciorna, setCiorna] = useState(String(afisat));
  useEffect(() => { setCiorna(String(afisat)); }, [afisat]);
  const trimite = () => {
    const v = Number(ciorna.replace(",", "."));
    if (!Number.isFinite(v) || v < 0) { setCiorna(String(afisat)); return; }
    const real = procent ? v / 100 : v;
    if (real !== valoare) laSchimbare(k, real);
  };
  return (
    <span className="ip-inp ip-inp-liber">
      <input type="number" inputMode="decimal" step={FORMA_LIBERA[k].pas} min={0} value={ciorna}
             aria-label={t(FORMA_LIBERA[k].eticheta)} disabled={!poate}
             onChange={(e) => setCiorna(e.target.value)} onBlur={trimite}
             onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
      <span>{um}</span>
    </span>
  );
}

/** HOW THE MONEY FLOWS on this scenario, written by the server's figures:
 *  equity first, then the loan, then the sales. Nothing here is typed. */
function CumCurgBanii({ rez }: { rez: Rezumat }) {
  const t = useT();
  const i = rez.ipoteze;
  const m = (v: number) => baniMilioane(v);
  const pc = (v: number) => `${numar(v * 100, { maximumFractionDigits: 0 })} %`;
  const receptie = rez.ultimaIncasare - Math.round(i.perioadaVanzareLuni);
  const cuCredit = rez.credit > 0 && rez.primaTragere != null;
  const lunaCredit = cuCredit ? rez.primaTragere! + 1 : receptie;
  const sumaReceptie = rez.venituri * i.vandutLaReceptiePct;
  const text = cuCredit
    ? t("ip.cum_curg.text", {
      capital: m(rez.capitalPropriu), luna_credit: lunaCredit, credit: m(rez.credit), receptie,
      dobanzi: m(rez.dobanzi), comision: m(rez.comisionCredit), p_receptie: pc(i.vandutLaReceptiePct),
      suma_receptie: m(sumaReceptie),
      rambursare: rez.rambursatLa != null ? t("ip.cum_curg.rambursat", { luna: rez.rambursatLa }) : t("ip.cum_curg.nerambursat"),
      luni: numar(i.perioadaVanzareLuni, { maximumFractionDigits: 0 }) })
    : t("ip.cum_curg.fara_credit", {
      capital: m(rez.capitalPropriu), receptie, p_receptie: pc(i.vandutLaReceptiePct), suma_receptie: m(sumaReceptie),
      luni: numar(i.perioadaVanzareLuni, { maximumFractionDigits: 0 }) });
  const tot = Math.max(rez.ultimaIncasare, 1);
  const w = (n: number) => `${Math.max((n / tot) * 100, 0)}%`;
  const luniCapital = cuCredit ? lunaCredit - 1 : receptie;
  return (
    <div className="ip-cum">
      <div className="bc-lbl">{t("ip.cum_curg")} <span className="ip-cum-sub">· {t("ip.cum_curg.sub")}</span></div>
      <p>{text}</p>
      <div className="ip-bar" aria-hidden="true">
        <div className="ip-bar-capital" style={{ width: w(luniCapital) }}>{t("ip.cum_curg.capital", { n: luniCapital })}</div>
        {cuCredit && (
          <div className="ip-bar-credit" style={{ width: w(receptie - luniCapital) }}>
            {t("ip.cum_curg.credit", { a: lunaCredit, b: receptie })}</div>
        )}
        <div className="ip-bar-vanzari" style={{ width: w(rez.ultimaIncasare - receptie) }}>
          {t("ip.cum_curg.vanzari", { n: numar(i.perioadaVanzareLuni, { maximumFractionDigits: 0 }) })}</div>
      </div>
      <div className="ip-ax">
        <span>{t("ip.cum_curg.start")}</span>
        {cuCredit && <span>{t("ip.cum_curg.luna", { n: lunaCredit })}</span>}
        <span>{t("ip.cum_curg.receptie")} · {t("ip.cum_curg.luna", { n: receptie })}</span>
        <span>{t("ip.cum_curg.luna", { n: rez.ultimaIncasare })}</span>
      </div>
    </div>
  );
}

/** PROPUNEREA PIEȚEI pentru prețul apartamentelor: prețul net recomandat de
 *  profilul zonei («Vânzări în zonă»), cu ce îl susține. Stă lângă decizie și
 *  se aplică numai la clic — piața e un reper, prețul îl alege dezvoltatorul. */
function DinPiata({ r, valoare, poate, laFolosire }: {
  r: Recomandare; valoare: number; poate: boolean; laFolosire: (v: number) => void;
}) {
  const t = useT();
  const p = r.pret;
  const euro = (v: number) => `${numar(v, { maximumFractionDigits: 0 })} €`;
  const oferte = tn("pz.n_oferte#n", p.n);
  return (
    <div className="ip-hl ip-piata" title={t("ip.piata_t")}>
      <b>{t("ip.piata", { pret: euro(p.net_mp) })}</b>
      <span>{r.finisaje
        ? t("ip.piata_pe_finisaj", { oferte, clasa: t(`pz.fin.${r.finisaje.clasa}`), cerut: euro(p.cerut_mp) })
        : t("ip.piata_pe_toate", { oferte, cerut: euro(p.cerut_mp) })}</span>
      {valoare === p.net_mp
        ? <span className="ip-pill ip-pill-ok">{t("ip.piata_folosit")}</span>
        : poate && <button type="button" className="btn ip-foloseste" onClick={() => laFolosire(p.net_mp)}>
            {t("ip.piata_foloseste")}</button>}
    </div>
  );
}

/** Valoarea unui rând al rezumatului, cum se citește pe scurt. */
function valoareRand(r: Rand): string {
  if (r.cantitate != null) {
    const z = r.zecimale ?? 0;
    const q = r.procent ? `${numar(r.cantitate * 100, { maximumFractionDigits: Math.max(z - 2, 0) })} %`
      : r.monetar ? bani(r.cantitate, { zecimale: z })
        : numar(r.cantitate, { minimumFractionDigits: 0, maximumFractionDigits: z });
    return r.um && !r.procent && !r.monetar && r.um !== "—" ? `${q} ${r.um}` : r.monetar && r.um
      ? `${q}/${r.um.split("/").pop()}` : q;
  }
  return r.parametru ?? (r.valoare != null ? bani(r.valoare, { zecimale: 0 }) : "—");
}

/** Un rând citit, nu scris; `pret` = rând de cost, unde reperul e prețul
 *  unitar, nu cantitatea măsurată pe care se aplică. */
function RandCitit({ r, pret = false }: { r: Rand; pret?: boolean }) {
  const t = useT();
  return (
    <div className="ip-pr">
      <span><Termeni text={r.element} /></span>
      <span className="ip-v">{eEstimat(r) && <Estimat deCe={r.observatie} />}{pret ? r.parametru ?? valoareRand(r) : valoareRand(r)}</span>
      <div className="ip-s">
        <span className={`bc-src bc-src-${r.sursa}`}>{t(`bc.sursa.${r.sursa}`)}</span>
        <span><Termeni text={r.seSchimbaDin ?? r.observatie ?? ""} /></span>
        {r.semnal && <Note note={[r.semnal]} />}
      </div>
    </div>
  );
}

export default function PanouIpoteze({ rez, implicite, sc, potScrie, laRezultate, laScenarii, piata = null }: {
  /** Rezumatul scenariului curent. */
  rez: Rezumat;
  /** Ipotezele proiectului fără abaterile vreunui scenariu: «implicit» = egal cu ele. */
  implicite: Ipoteze;
  sc: Scenarii;
  potScrie: boolean;
  laRezultate: () => void;
  laScenarii: () => void;
  /** Configurația recomandată de profilul zonei, dacă proiectul are oferte
   *  culese; prețul ei se propune lângă prețul apartamentelor. */
  piata?: Recomandare | null;
}) {
  const t = useT();
  const i = rez.ipoteze;
  const verificate = sc.stare?.verificate ?? null;
  const validate = sc.stare?.validate ?? {};
  // durata din program n-are ce confirma: nu e o ipoteză
  const durataEaProgramului = (rez.sectiuni.flatMap((s) => s.randuri).find((r) => r.cheie === "durata")?.sursa
    ?? null) === "proiect";
  const deValidat = DECIZII.filter((k) => i[k] === implicite[k] && !validate[k]
    && !(k === "duratiAni" && durataEaProgramului));
  const schimba = (k: Decizie | Liber | "dobandaPlusEuribor", v: number) => { void sc.schimba({ [k]: v }).catch(() => {}); };
  const valideaza = (k: Decizie) => {
    const ultima = deValidat.length === 1 && deValidat[0] === k;
    void sc.valideaza(k, ultima).catch(() => {});
  };
  const camp = (k: Decizie) => (
    <Camp k={k} valoare={i[k]} implicit={implicite[k]} poate={potScrie}
          verificate={!!verificate} validata={validate[k] ?? null}
          laSchimbare={schimba} laValidare={valideaza} />
  );
  // DURATA E A PROGRAMULUI cât există unul: rândul ei din rezumat vine atunci cu
  // sursa «proiect», iar aici se citește, nu se scrie — se schimbă din fila
  // «Program» (macarale pe perioade, efective, lag-uri)
  const randDurata = rez.sectiuni.flatMap((s) => s.randuri).find((r) => r.cheie === "durata") ?? null;
  const durataDinProgram = randDurata?.sursa === "proiect";
  const rand = (k: Decizie, extra?: ReactNode) => (
    <div className="ip-row" key={k}>
      <div>
        <div className="ip-nm"><Termeni text={t(FORMA[k].eticheta)} />
          {k === "duratiAni" && durataDinProgram
            ? <Lamurire text={t("ip.ajutor.durata_program")} />
            : FORMA[k].ajutor && <Lamurire text={t(FORMA[k].ajutor!)} />}</div>
        {k === "pretApartamenteMp" && piata && (
          <DinPiata r={piata} valoare={i[k]} poate={potScrie} laFolosire={(v) => schimba(k, v)} />
        )}
        {extra}
      </div>
      {k === "duratiAni" && durataDinProgram && randDurata ? (
        <span className="ip-ctl">
          <span className="ip-pill ip-pill-ok">{t("ip.din_program")}</span>
          <span className="ip-inp"><span className="ip-v">{valoareRand(randDurata)}</span>
            <span className="bc-um">{randDurata.observatie ?? ""}</span></span>
        </span>
      ) : camp(k)}
    </div>
  );

  const toate = rez.sectiuni.filter((s) => s.cheie === "ipoteze" || s.cheie === "cantitati")
    .flatMap((s) => s.randuri).filter((r) => !RANDURI_DECIZII.includes(r.cheie));
  const dinProiect = toate.filter((r) => DIN_PROIECT.includes(r.sursa));
  const costuri = rez.sectiuni.find((s) => s.cheie === "costuri")?.randuri ?? [];
  const repere = toate.filter((r) => !DIN_PROIECT.includes(r.sursa));
  const repereCost = costuri.filter((r) => (r.sursa === "referinta" || r.sursa === "ipoteza")
    && !RANDURI_DECIZII.includes(r.cheie));

  return (
    <div className="ip">
      <div className="ip-sus">
        <span className="bc-lbl">{t("ip.scenariul")}</span>
        <b>{sc.curent.nume}</b>
        <button type="button" className="btn btng" onClick={laScenarii}>{t("ip.alt_scenariu")}</button>
        <span style={{ flex: 1 }} />
        {sc.seSalveaza && <span className="bc-um">{t("comun.se_salveaza")}</span>}
        {sc.eroare && <span className="ip-err">{sc.eroare}</span>}
      </div>

      <div className="ip-grid">
        <section className="ip-card" aria-labelledby="ip-dec">
          <div className="ip-card-h"><h2 id="ip-dec">{t("ip.ce_decizi")}</h2>
            <span className="bc-um"><Termeni text={t("ip.ce_decizi_sub")} /></span></div>

          <div className="ip-grp"><span className="bc-lbl">{t("ip.grup.teren")}</span></div>
          {rand("cedatDinScd")}
          {rand("pretTerenBani")}
          {rand("costuriTeren")}

          <div className="ip-grp"><span className="bc-lbl">{t("ip.grup.vanzari")}</span></div>
          {rand("pretApartamenteMp")}
          <div className="ip-row">
            <div><div className="ip-nm">{t("ip.banda_pret")}<Lamurire text={t("ip.ajutor.banda_pret")} /></div></div>
            <span className="ip-ctl">
              <CampLiber k="bandaPretPct" valoare={i.bandaPretPct} poate={potScrie} procent um="± %" laSchimbare={schimba} />
            </span>
          </div>
          {rand("pretTeraseMp")}
          {rand("pretComercialMp")}
          {rand("pretParcareLoc")}
          {/* THE PACE OF SALES as one sentence with two numbers in it: the share
              cashed at completion, the months the rest takes */}
          <div className="ip-row ip-row-fraza">
            <div>
              <div className="ip-nm">{t("ip.ritm")}<Lamurire text={t("ip.ajutor.ritm")} /></div>
              <div className="ip-fraza">
                <span>{t("ip.ritm.la_receptie")}</span>
                {camp("vandutLaReceptiePct")}
                <span>{t("ip.ritm.din_venituri")}</span>
                {camp("perioadaVanzareLuni")}
                <span>{t("ip.ritm.dupa")}</span>
              </div>
            </div>
          </div>

          <div className="ip-grp"><span className="bc-lbl">{t("ip.grup.finantare")}</span></div>
          {rand("creditDinCostPct", <div className="ip-hl">{t("ip.credit_suma", { suma: baniMilioane(rez.credit) })}</div>)}
          {/* THE RATE: the margin is the decision; «+ Euribor» and the Euribor
              figure stand beside it, and the sum is written */}
          <div className="ip-row ip-row-fraza">
            <div>
              <div className="ip-nm">{t("ip.dobanda")}<Lamurire text={t("ip.ajutor.dobanda")} /></div>
              <div className="ip-fraza">
                <span>{t("ip.dobanda.marja")}</span>
                {camp("dobandaMarjaPct")}
                <label className="ip-bifa">
                  <input type="checkbox" checked={i.dobandaPlusEuribor >= 0.5} disabled={!potScrie}
                         onChange={(e) => schimba("dobandaPlusEuribor", e.target.checked ? 1 : 0)} />
                  <span>{t("ip.dobanda.plus_euribor")}</span>
                </label>
                <CampLiber k="euriborPct" valoare={i.euriborPct} poate={potScrie && i.dobandaPlusEuribor >= 0.5} procent um="%"
                           laSchimbare={schimba} />
                <b>{t("ip.dobanda.egal", { p: `${numar(rez.rataAn * 100, { maximumFractionDigits: 2 })} %` })}</b>
              </div>
            </div>
          </div>
          {rand("comisionCreditPct")}
          <CumCurgBanii rez={rez} />

          <div className="ip-grp"><span className="bc-lbl">{t("ip.grup.calendar")}</span></div>
          {rand("duratiAni")}
        </section>

        <section className="ip-card" aria-labelledby="ip-pr">
          <div className="ip-card-h"><h2 id="ip-pr">{t("ip.din_proiect")}</h2>
            <Lamurire text={t("ip.din_proiect_sub")} /></div>
          {dinProiect.map((r) => <RandCitit key={r.cheie} r={r} />)}
        </section>
      </div>

      <section className="ip-card" aria-labelledby="ip-rep">
        <div className="ip-card-h"><h2 id="ip-rep">{t("ip.repere")}</h2>
          <Lamurire text={t("ip.repere_sub")} /></div>
        <div className="ip-rep">
          {repere.map((r) => <RandCitit key={r.cheie} r={r} />)}
          {repereCost.map((r) => <RandCitit key={r.cheie} r={r} pret />)}
        </div>
      </section>

      <div className="ip-foot">
        <span className="bc-um" style={{ flex: "1 1 320px" }}>
          {verificate
            ? t("ip.verificate_de", { de: verificate.de || "—",
                la: verificate.la ? new Date(verificate.la).toLocaleDateString() : "" })
            : t("ip.raman_implicite", { n: deValidat.length, tot: DECIZII.length })}
        </span>
        {verificate ? (
          <>
            {potScrie && <button type="button" className="btn btng"
                                 onClick={() => { void sc.verifica(false).catch(() => {}); }}>
              {t("ip.retrage")}</button>}
            <button type="button" className="btn btnp" onClick={laRezultate}>{t("ip.la_rezultate")}</button>
          </>
        ) : (
          <>
            <button type="button" className="btn btng" onClick={laRezultate}>{t("ip.sari")}</button>
            <button type="button" className="btn btnp" disabled={!potScrie}
                    onClick={() => { void sc.verifica(true).then(laRezultate).catch(() => {}); }}>
              {t("ip.am_verificat")}</button>
          </>
        )}
      </div>
    </div>
  );
}
